/**
 * Which video a session was watching and how far, kept in the browser because the video never
 * leaves it. Plain .ts so `node --test` can load it; the storage comes in as an argument.
 */
import { audioNameFor } from "./videoOpening.ts";

export interface VideoBinding {
	name: string;
	size: number;
	seconds: number;
	/** The audio of this file already went up, so choosing it again extracts nothing. */
	audioUploaded: boolean;
	/** Where the upload put the audio, which may not be the name it was sent under. */
	audioPath?: string;
	/** The panel already said this video is finished, so reaching the end again says nothing. */
	learned?: boolean;
	/** The stops already made, by their time, so playing past one again stops nowhere. */
	stopsFired?: number[];
}

export type BindingStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const keyOf = (sessionId: string) => `hifi.video.${sessionId}`;

function isBinding(value: unknown): value is VideoBinding {
	const v = value as VideoBinding | null;
	return !!v && typeof v.name === "string" && typeof v.size === "number" && typeof v.seconds === "number" && typeof v.audioUploaded === "boolean" && (v.audioPath === undefined || typeof v.audioPath === "string") && (v.learned === undefined || typeof v.learned === "boolean") && (v.stopsFired === undefined || (Array.isArray(v.stopsFired) && v.stopsFired.every((n) => typeof n === "number")));
}

/** Null when there is none, and when storage is blocked or holds something else. */
export function readBinding(store: BindingStore, sessionId: string): VideoBinding | null {
	try {
		const value: unknown = JSON.parse(store.getItem(keyOf(sessionId)) ?? "null");
		return isBinding(value) ? value : null;
	} catch {
		return null;
	}
}

/** Best effort: the panel works without storage, it only forgets the place. */
export function writeBinding(store: BindingStore, sessionId: string, binding: VideoBinding): void {
	try {
		store.setItem(keyOf(sessionId), JSON.stringify(binding));
	} catch {
		// storage is blocked or full
	}
}

/** For a video they said they are done with, so the session stops offering it. */
export function clearBinding(store: BindingStore, sessionId: string): void {
	try {
		store.removeItem(keyOf(sessionId));
	} catch {
		// storage is blocked
	}
}

export function matchesBinding(binding: VideoBinding, file: { name: string; size: number }): boolean {
	return binding.name === file.name && binding.size === file.size;
}

/** The bound file chosen again keeps its place; any other file starts over. */
export function bindFile(previous: VideoBinding | null, file: { name: string; size: number }): VideoBinding {
	if (previous && matchesBinding(previous, file)) return previous;
	return { name: file.name, size: file.size, seconds: 0, audioUploaded: false };
}

export function withStopFired(binding: VideoBinding, at: number): VideoBinding {
	const fired = binding.stopsFired ?? [];
	return fired.includes(at) ? binding : { ...binding, stopsFired: [...fired, at] };
}

/** Null until the audio went up. A binding older than `audioPath` names the audio after the video. */
export function audioPathOf(binding: VideoBinding): string | null {
	if (!binding.audioUploaded) return null;
	return binding.audioPath ?? `uploads/${audioNameFor(binding.name)}`;
}

const VIDEO_EXTENSION = /\.(mp4|m4v|mov|mkv|webm|avi|flv|wmv|mpg|mpeg)$/i;

/** By type, or by extension where the browser gives none, as it does for `.mkv` outside Chrome. */
export function isVideoFile(file: { name: string; type: string }): boolean {
	return file.type.startsWith("video/") || (!file.type && VIDEO_EXTENSION.test(file.name));
}
